import { Typography } from '@design-system/components/typography/typography';
import { PropsWithChildren, useEffect, useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@design-system/components/button/button';
import { IconButton } from '@design-system/components/button/iconButton';
import { faArrowLeft, faFolderPlus } from '@fortawesome/pro-regular-svg-icons';
import { Breadcrumbs } from '@mui/material';
import { Breadcrumb, LibraryVM } from '../view-models/library.viewModel';
import { isEmpty } from 'lodash';
import { Async } from '@client/new/helpers/types';
import { clsx } from 'clsx';
import { LibraryError } from './LibraryError';

const getBackFolder = (
  breadcrumbs: Breadcrumb[],
  p: PropsWithChildren<Async<LibraryVM>>,
) => {
  if (p.type == 'fetched') {
    const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
    return (
      p.value.allFolders.find((i) => i.id === previousBreadcrumb.id) || null
    );
  }
  return null;
};

export const PageWrapper = (p: PropsWithChildren<Async<LibraryVM>>) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [pageTitle, setPageTitle] = useState<string>('Library');

  useEffect(() => {
    if (p.type == 'fetched') {
      setBreadcrumbs(p.value.breadcrumbs);
      const title = isEmpty(p.value.breadcrumbs)
        ? 'Library'
        : p.value.breadcrumbs[p.value.breadcrumbs.length - 1].label;
      setPageTitle(title);
    }
  }, [p]);

  const fetched = p.type === 'fetched';
  const previousFolder =
    isEmpty(breadcrumbs) || breadcrumbs.length == 1
      ? null
      : getBackFolder(breadcrumbs, p);

  return (
    <div className="px-25 lg:px-[60px] h-[calc(100%-40px)] max-w-[1600px]">
      {isEmpty(breadcrumbs) ? null : (
        <div className="mb-8">
          <Breadcrumbs>
            <Typography
              className={clsx(
                'cursor-pointer',
                !fetched && 'pointer-events-none cursor-wait',
              )}
              onClick={() =>
                fetched && p.value.dispatch('breadcrumbClicked', null)
              }
            >
              Library
            </Typography>
            {breadcrumbs.map((item, index) => (
              <Typography
                key={item.label}
                onClick={item.onClick}
                className={clsx(
                  index === breadcrumbs.length - 1 && 'cursor-auto text-black',
                  index !== breadcrumbs.length - 1 &&
                    'cursor-pointer text-[#646464]',
                  !fetched && 'pointer-events-none cursor-wait',
                )}
                variant="body"
                size="large"
              >
                {item.label}
              </Typography>
            ))}
          </Breadcrumbs>
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row justify-between">
        <div className="flex items-center gap-4">
          {isEmpty(breadcrumbs) ? null : (
            <IconButton
              icon={faArrowLeft}
              className={clsx(
                'bg-white text-black',
                !fetched && 'pointer-events-none cursor-wait',
              )}
              disabled={!fetched}
              onClick={() =>
                fetched && p.value.dispatch('breadcrumbClicked', previousFolder)
              }
            />
          )}
          <Typography variant="headline" size="large" id="page-title">
            {pageTitle}
          </Typography>
        </div>
        {fetched && p.value.emptyLibrary ? null : (
          <div className="flex items-center gap-6">
            <Button
              leftIcon={faFolderPlus}
              variant="text"
              size="large"
              color="neutral"
              disabled={!fetched}
              onClick={() => fetched && p.value.dispatch('addFolderModal')}
            >
              Folder
            </Button>
            <Button
              leftIcon={faPlus}
              variant="filled"
              size="large"
              color="neutral"
              disabled={!fetched}
              onClick={() => fetched && p.value.dispatch('openUploadModal')}
            >
              Upload
            </Button>
          </div>
        )}
      </div>
      {p.type === 'fetch-error' ? (
        <LibraryError />
      ) : (
        <div className="my-5 2xl:my-10 h-full">{p.children}</div>
      )}
    </div>
  );
};
